import type { JSX } from 'react';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import * as Text from '../../../common/i18n/Text.ts';
import * as OperatorLoginsState from '../../../common/state/OperatorLoginsState.ts';
import * as PermitTypeState from '../../../common/state/PermitTypeState.ts';
import { FilterButtonWithSummary } from '../../../ui/filter/FilterButtonWithSummary.tsx';
import {
    FilterSummaryItem,
    FilterSummaryItemSubTitle,
    FilterSummaryItemTitle,
} from '../../../ui/filter/FilterSummary.tsx';
import * as FilteredPermitsActions from '../../actions/FilteredPermitsActions.ts';
import {
    filterSummaryTexts,
    issueDateFilterTexts,
    operatorLoginFilterTexts,
    permitTypeFilterTexts,
} from '../../i18n/FilteredPermitsTexts.ts';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';
import { CostFilterSummary } from './CostFilter.tsx';
import { operatorLoginSelection } from './OperatorLoginFilter.tsx';
import { permitTypeSelection } from './PermitTypeFilter.tsx';
import { StatusFilterSummary } from './StatusFilter.tsx';
import { TimeLimitFilterSummary } from './TimeLimitFilter.tsx';
import { ChannelFilterSummary } from './ChannelFilter.tsx';

export interface FilterSummaryTexts {
    validDate: Text.Translation;
    zone: Text.Translation;
}

export function FilteredPermitsFilterSummary() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        filterSummary: FilteredPermitsState.FilterSummary.get(store),
    }));
    const { language } = useLanguage();

    const texts = filterSummaryTexts[language];
    return (
        <FilterButtonWithSummary
            onFilterClick={() =>
                update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        filterSlideInVisible: true,
                    }),
                )
            }
            clearFilter={() => update(FilteredPermitsActions.clearFilter)}
        >
            {FilteredPermitsState.Filter.filterActive(storeState.filter) && (
                <>
                    <ValidDateFilterSummary
                        filter={storeState.filter}
                        texts={texts}
                    />
                    <PermitTypeFilterSummary />
                    <OperatorLoginFilterSummary />
                    <IssueDateFilterSummary />
                    <TimeLimitFilterSummary />
                    <ChannelFilterSummary />
                    <CostFilterSummary />
                    <StatusFilterSummary />
                </>
            )}
        </FilterButtonWithSummary>
    );
}

interface SummaryElementProps {
    filter: FilteredPermitsState.Filter.State;
    texts: FilterSummaryTexts;
}

const ValidDateFilterSummary = (p: SummaryElementProps): JSX.Element => (
    <Conditional
        c={FilteredPermitsState.Filter.validDateFilterActive(p.filter)}
    >
        <FilterSummaryItem hasSeparator={false}>
            <FilterSummaryItemTitle title={p.texts.validDate()} />
            <FilterSummaryItemSubTitle
                title={
                    '\u00A0' +
                    Formatter.openRange(
                        p.filter.validDateFrom,
                        p.filter.validDateTo,
                        Formatter.dayMonthYear,
                    )
                }
            />
        </FilterSummaryItem>
    </Conditional>
);

export function PermitTypeFilterSummary() {
    const { storeState } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        permitTypes: PermitTypeState.get(store),
    }));
    const { language } = useLanguage();

    return (
        <Conditional
            c={FilteredPermitsState.Filter.permitTypeFilterActive(
                storeState.filter,
            )}
        >
            <FilterSummaryItem
                hasSeparator={FilteredPermitsState.Filter.validDateFilterActive(
                    storeState.filter,
                )}
            >
                <FilterSummaryItemTitle
                    title={permitTypeFilterTexts[language].filterTitle()}
                />
                <FilterSummaryItemSubTitle
                    title={
                        '\u00A0' +
                        permitTypeSelection(
                            storeState.permitTypes.data,
                            storeState.filter.permitTypes,
                        )
                    }
                />
            </FilterSummaryItem>
        </Conditional>
    );
}

export function OperatorLoginFilterSummary() {
    const { storeState } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        operatorLogins: OperatorLoginsState.List.get(store),
    }));
    const { language } = useLanguage();

    return (
        <Conditional
            c={FilteredPermitsState.Filter.operatorLoginFilterActive(
                storeState.filter,
            )}
        >
            <FilterSummaryItem
                hasSeparator={
                    FilteredPermitsState.Filter.validDateFilterActive(
                        storeState.filter,
                    ) ||
                    FilteredPermitsState.Filter.permitTypeFilterActive(
                        storeState.filter,
                    )
                }
            >
                <FilterSummaryItemTitle
                    title={operatorLoginFilterTexts[language].filterTitle()}
                />
                <FilterSummaryItemSubTitle
                    title={
                        '\u00A0' +
                        operatorLoginSelection(
                            storeState.operatorLogins.data,
                            storeState.filter.operatorLogins,
                        )
                    }
                />
            </FilterSummaryItem>
        </Conditional>
    );
}

export function IssueDateFilterSummary() {
    const { storeState } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    return (
        <Conditional
            c={FilteredPermitsState.Filter.issueDateFilterActive(
                storeState.filter,
            )}
        >
            <FilterSummaryItem
                hasSeparator={
                    FilteredPermitsState.Filter.validDateFilterActive(
                        storeState.filter,
                    ) ||
                    FilteredPermitsState.Filter.permitTypeFilterActive(
                        storeState.filter,
                    ) ||
                    FilteredPermitsState.Filter.operatorLoginFilterActive(
                        storeState.filter,
                    )
                }
            >
                <FilterSummaryItemTitle
                    title={issueDateFilterTexts[language].filterTitle()}
                />
                <FilterSummaryItemSubTitle
                    title={
                        '\u00A0' +
                        Formatter.openRange(
                            storeState.filter.issueDateFrom,
                            storeState.filter.issueDateTo,
                            Formatter.dayMonthYear,
                        )
                    }
                />
            </FilterSummaryItem>
        </Conditional>
    );
}
